import React, { useEffect, useState, createContext } from 'react';
import mapboxgl from 'mapbox-gl';
import { WxTileSource } from '@metoceanapi/wxtiles-mbox/src/wxsource/wxsource';
import { IStage } from '@metservicedev/platforms-services-interface/src';

import { DateTime } from 'luxon';
import { useSettingsContext } from '@mono/platmap-settings-context';
import { diff } from '@mono/diff';
import { Layer, WXTilesV3Layer, PlatmapLayerContext } from '@mono/types/src/platmap';
import { useWxtilesV3 } from '@mono/use-wxtiles-v3';
import { toHash } from '@mono/to-hash';

import { stateToStyle } from './stateToStyle';
import { useTimeContext } from '../time-context';
import { isWXTilesV3Layer } from 'layerTypeUtils';

export const WXTilesV3ServiceContext = createContext<
    PlatmapLayerContext & {
        layers: any[] | null;
        wxSources: { [layerId: string]: WxTileSource };
    }
>({
    layers: null,
    setMap: () => {},
    shuffle: () => {},
    wxSources: {},
});

const getWxtilesv3LayerIndex = (layer: Layer & { state: WXTilesV3Layer }, delimiter: string) =>
    ['plat', 'wxtilesv3', layer.id, layer.state.datasetId, layer.state.variable].join(delimiter);

export const WXTilesV3ServiceContextProvider = ({
    children,
    delimiter,
    anchorId,
    stage,
}: {
    children: JSX.Element;
    delimiter: string;
    anchorId: string;
    stage: IStage;
}) => {
    const { selectedTime, timespan, timePoints, setIsLoading } = useTimeContext();
    const [state, setState] = useState<{
        map: mapboxgl.Map | null;
        shuffleIndex: string;
    }>({
        map: null,
        shuffleIndex: 'The sqrt(-1), characterised as i, was imagined first - discovered in nature second',
    });
    const [wxSources, setWxSources] = useState<{ [layerId: string]: WxTileSource }>({});

    const { settings } = useSettingsContext();
    const wxtilesV3Layers = settings.layers.filter(isWXTilesV3Layer);
    const wxtilesV3DatasetIds = wxtilesV3Layers.map((l) => l.state.datasetId);

    const { wxDatasetManagers, createNewWxSource } = useWxtilesV3({ datasetsToExpand: wxtilesV3DatasetIds, stage, refreshTime: 1000 * 60 });

    const [bbox, setBbox] = useState<[number, number, number, number]>();
    useEffect(() => {
        if (!state.map) return;

        const onMoveEnd = (e: mapboxgl.MapboxEvent) => {
            const bounds = state.map?.getBounds();
            if (!bounds) return;
            setBbox([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);
        };

        state.map.on('moveend', onMoveEnd);
        return () => {
            if (!state.map) return;
            state.map.off('moveend', onMoveEnd);
        };
    }, [state.map]);

    useEffect(() => {
        if (!state.map) return;
        const mapboxStyle = state.map.getStyle();
        if (!mapboxStyle) return;
        const mapboxLayers = mapboxStyle.layers;
        if (!selectedTime) return;
        if (!mapboxLayers) return;

        const existingLayers = mapboxLayers
            .filter((l) => {
                if (!l.id.startsWith(['plat', 'wxtilesv3', ''].join(delimiter))) return false;
                if (l.id.includes(anchorId)) return false;
                return true;
            })
            .map((a) => a.id);

        const targetLayers = settings.layers
            .filter(isWXTilesV3Layer)
            .map((a) => {
                const wxDataset = wxDatasetManagers[a.state.datasetId];
                if (!wxDataset) return null;
                const bestTime = wxDataset.getNearestValidTime(selectedTime);
                if (bestTime === null) return null;

                return getWxtilesv3LayerIndex(a, delimiter);
            })
            .filter((a): a is string => !!a);

        const { add, remove } = diff(existingLayers, targetLayers);

        for (const layerIndex of remove) {
            if (!state.map) return;
            state.map.removeLayer(layerIndex);
            state.map.removeSource(layerIndex);
            setWxSources((prev) => {
                const { [layerIndex]: _, ...rest } = prev;
                return rest;
            });
        }

        // This if is a poll to check if the anchor layers have finished being added to mapbox.
        // Better to subscribe and be notified, but mapboxgl doesn't support that. They'd be a lot cooler if they did.
        if (
            existingLayers.some((layerIndex) => {
                const [, , layerId] = layerIndex.split(delimiter);
                const anchorIndex = ['plat', 'wxtilesv3', layerId, anchorId].join(delimiter);

                return !mapboxLayers.find((l) => l.id === anchorIndex);
            })
        ) {
            setTimeout(() => {
                setState((prev) => ({ ...prev, shuffleIndex: prev.shuffleIndex + 'x' }));
            }, 16);
            return;
        }

        for (const layerIndex of add) {
            const [, , layerId] = layerIndex.split(delimiter);
            const anchorIndex = ['plat', 'wxtilesv3', layerId, anchorId].join(delimiter);

            if (mapboxStyle.sources[layerIndex]) continue;
            if (!layerId) continue;

            const layer = wxtilesV3Layers.find((w) => w.id === layerId);
            if (!layer) continue;

            const wxDataset = wxDatasetManagers[layer.state.datasetId];
            if (!wxDataset) continue;
            if (!layer.state.variable) continue;

            const bestTime = wxDataset.getNearestValidTime(selectedTime);

            const wxSource = createNewWxSource(
                {
                    //@ts-ignore todo mapboxgl versions need synchronizing
                    map: state.map,
                    wxstyleName: 'base',
                    wxdatasetManager: wxDataset,
                    variables: Array.isArray(layer.state.variable) ? layer.state.variable : [layer.state.variable],
                    time: bestTime ?? -1,
                },
                {
                    id: wxDataset.datasetName + '-source',
                }
            );
            wxSource.updateCurrentStyleObject(stateToStyle(layer.state));
            setWxSources((prev) => ({ ...prev, [layerId]: wxSource }));

            state.map.addSource(layerIndex, wxSource);

            state.map.addLayer(
                {
                    id: layerIndex,
                    type: 'raster',
                    source: layerIndex,
                    paint: { 'raster-fade-duration': 0 },
                },
                anchorIndex
            );
        }

        for (const layer of settings.layers) {
            if (layer.state.type !== 'wxtilesv3') continue;
            const layers = mapboxStyle.layers
                .map((l) => l.id)
                .filter((l) => l.startsWith(['plat', 'wxtilesv3', layer.id].join(delimiter)))
                .filter((l) => !l.includes('anchor'));
            if (!layers) continue;

            const layerAnchorId = ['plat', 'wxtilesv3', layer.id, anchorId].join(delimiter);
            for (const layerIndex of layers) {
                state.map.getLayer(layerIndex) && state.map.moveLayer(layerIndex, layerAnchorId);
            }
        }
    }, [state.shuffleIndex, Object.keys(wxDatasetManagers).join(), wxtilesV3Layers.map((l) => JSON.stringify(l.state.styleOverrides)).join(), selectedTime]);

    useEffect(() => {
        if (!state.map) return;
        if (!selectedTime) return;

        settings.layers
            .filter((l): l is Layer & { state: WXTilesV3Layer } => {
                if (l.state.type !== 'wxtilesv3') return false;
                return true;
            })
            .forEach((layer) => {
                const wxSource = wxSources[layer.id];
                if (!wxSource) return;
                const wxDataset = wxDatasetManagers[layer.state.datasetId];
                if (!wxDataset) return;

                const bestTime = wxDataset.getNearestValidTime(selectedTime);
                if (bestTime === null) return;
                wxSource.setTime(bestTime);
            });
    }, [state.map, selectedTime, Object.keys(wxDatasetManagers).join()]);

    useEffect(() => {
        if (!state.map) return;
        if (!timespan) return;
        if (!timePoints) return;

        const firstTimePoint = DateTime.fromISO(timePoints[0]).setZone('UTC').toISO();
        const lastTimePoint = DateTime.fromISO(timePoints[timePoints.length - 1])
            .setZone('UTC')
            .toISO();
        const wxSourceTimes = Object.keys(wxSources).map((wxSourceKey) => {
            const wxSource = wxSources[wxSourceKey];
            const times = wxSource.wxdatasetManager.getAllTimes();

            return {
                id: wxSourceKey,
                times: times.filter((t) => {
                    if (t < firstTimePoint) return false;
                    if (t > lastTimePoint) return false;
                    return true;
                }),
            };
        });

        if (wxSourceTimes.length === 0) {
            setIsLoading((prev) => {
                return {
                    ...prev,
                    wxtilesV3: false,
                };
            });
            return;
        }

        const abortController = new AbortController();

        (async () => {
            setIsLoading((prev) => {
                return {
                    ...prev,
                    wxtilesV3: true,
                };
            });
            const allPreloads = wxSourceTimes
                .map((wxSourceTime) => {
                    const wxSource = wxSources[wxSourceTime.id];
                    if (!wxSource) return;

                    return wxSourceTime.times.map((t) => {
                        return wxSource.preloadTime(t, { signal: abortController.signal });
                    });
                })
                .flat();
            await Promise.all(allPreloads);

            setIsLoading((prev) => {
                return {
                    ...prev,
                    wxtilesV3: false,
                };
            });
        })();
        return () => {
            abortController.abort();
        };
    }, [timespan, state.map, Object.keys(wxSources).join(), toHash(bbox)]);

    useEffect(() => {
        if (!state.map) return;

        settings.layers
            .filter((l): l is Layer & { state: WXTilesV3Layer } => {
                if (l.state.type !== 'wxtilesv3') return false;
                if (!l.state.variable) return false;

                return true;
            })
            .forEach((layer) => {
                const wxSource = wxSources[layer.id];
                if (!wxSource) return;

                const style = stateToStyle(layer.state);

                wxSource.updateCurrentStyleObject(style);
            });
    }, [state.map, wxtilesV3Layers.map((l) => toHash([l.state.styleOverrides, l.state.levels])).join()]);

    return (
        <WXTilesV3ServiceContext.Provider
            value={{
                setMap: (map: mapboxgl.Map) => setState((state) => ({ ...state, map })),
                shuffle: (shuffleIndex: string) => {
                    setState((state) => ({ ...state, shuffleIndex }));
                },
                layers: wxtilesV3Layers,
                wxSources,
            }}
        >
            {children}
        </WXTilesV3ServiceContext.Provider>
    );
};
